import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";
import { SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { refetchAllAppointmentsApis } from "../../api/Appointments/refetchAllAppointmentsApis";
import EmptyImage from "../../assets/images/icons/appointment-empty.svg";
import { GeneralButton, GeneralCalendar, Loader } from "../../components";
import { EndPoints } from "../../Constants/endpoints";
import { useAppSelector, useGetData, usePostData } from "../../hooks";
import {
  IAppointmentFormData,
  IAvailableScheduled,
  IScheduledDays,
  IScheduledTime,
} from "../../interface/appointmentInterfaces";
import { PatientDetails } from "../../interface/GeneralResponse";
import { RootState } from "../../redux/store";
import { TitleWithArrow } from "../../Share";
import { AppointmentMessage, AppointmentPatientDetailsForm } from "./component";

interface AppointmentPageLocationState {
  patient?: PatientDetails;
}

interface AppointmentPageData {
  Account?: PatientDetails;
  appointmentDate?: string;
  appointmentTime?: string;
}

function AddNewAppointmentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const patient = (location.state as AppointmentPageLocationState | null)
    ?.patient;

  const registration = useAppSelector((state: RootState) => state.registration);

  const countryId = Number(registration?.userDetails?.CountryId);

  const [appointmentFormData, setAppointmentFormData] =
    useState<AppointmentPageData>({
      Account: patient,
      appointmentDate: moment().locale("en").format("YYYY-MM-DD"),
      appointmentTime: undefined,
    });

  const [isScheduleLoading, setIsScheduleLoading] = useState<boolean>(false);
  const [isSuccessMessageOpen, setIsSuccessMessageOpen] =
    useState<boolean>(false);

  const [isErrorMessageOpen, setIsErrorMessageOpen] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string>("");

  // State to track patient from child component (for URL id case)
  const [patientFromChild, setPatientFromChild] = useState<
    PatientDetails | undefined
  >(undefined);

  // Get the doctor's scheduled working days.
  const { data: scheduledDaysData } = useGetData<IScheduledDays[]>(
    EndPoints.GetScheduledDays,
  );

  // Get the available times for the selected date.
  const { data: availableScheduledData, isLoading: isAvailableTimesLoading } =
    useGetData<IAvailableScheduled>(EndPoints.GetAvailableScheduled, {
      filter: {
        date: moment(appointmentFormData.appointmentDate ?? new Date())
          .locale("en")
          .format("YYYY-MM-DD"),
      },
    });

  // Create patient request.
  const { mutate: createPatient, isPending: isCreatingPatient } = usePostData(
    EndPoints.CreatePatient,
  );

  // Create appointment request.
  const { mutate: createAppointment, isPending: isCreatingAppointment } =
    usePostData(EndPoints.AddAppointmentForPatient);

  const scheduledDays =
    scheduledDaysData?.Data?.map((item: IScheduledDays) => item?.Name) ?? [];

  const availableTimes = availableScheduledData?.Data?.Schedules ?? [];

  const isSubmitting = isCreatingPatient || isCreatingAppointment;

  const selectedDate = appointmentFormData.appointmentDate;

  const selectedTime = appointmentFormData.appointmentTime;

  // Get the current patient (from location state or from child component)
  const currentPatient =
    patientFromChild || patient || appointmentFormData.Account;

  // Format a time such as 16:30:00 to 04:30 PM.
  const formatTime = (time?: string) => {
    if (!time) {
      return "";
    }

    return moment(time, ["HH:mm:ss", "HH:mm"]).format("hh:mm A");
  };

  // Generate one Moment value from the selected
  // calendar date and selected appointment time.
  const getSelectedDateTime = () => {
    if (!selectedDate || !selectedTime) {
      return null;
    }

    return moment(
      `${moment(selectedDate).format("YYYY-MM-DD")} ${selectedTime}`,
      ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"],
    );
  };

  // Disable times that have already passed today.
  const isPastTime = (time: string) => {
    if (!selectedDate) {
      return false;
    }

    const selectedDateTime = moment(
      `${moment(selectedDate).format("YYYY-MM-DD")} ${time}`,
      ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"],
    );

    return selectedDateTime.isBefore(moment());
  };

  // Select date from GeneralCalendar.
  const handleSelectDate = (date: string) => {
    const normalizedDate = moment(date).locale("en").format("YYYY-MM-DD");

    const isPastDay = moment(normalizedDate).isBefore(moment(), "day");

    const dayName = moment(normalizedDate).format("dddd");

    const hasNoSchedule =
      scheduledDays.length > 0 && !scheduledDays.includes(dayName);

    if (isPastDay || hasNoSchedule) {
      return;
    }

    setIsScheduleLoading(true);

    setAppointmentFormData((previous) => ({
      ...previous,
      appointmentDate: normalizedDate,
      appointmentTime: undefined,
    }));

    window.setTimeout(() => {
      setIsScheduleLoading(false);
    }, 300);
  };

  //Select an available appointment time.
  const handleSelectTime = (schedule: IScheduledTime) => {
    const disabled = !schedule?.isAvailable || isPastTime(schedule?.time);

    if (disabled) {
      return;
    }

    setAppointmentFormData((previous) => ({
      ...previous,
      appointmentTime: schedule.time,
    }));
  };

  // Handle API errors.
  const handleApiError = (error: any) => {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.Message ||
      error?.message ||
      t("something_went_wrong");

    setErrorMessage(message);
    setIsErrorMessageOpen(true);
  };

  // Create the appointment after obtaining the patient ID.
  const bookAppointment = (patientId: number) => {
    const appointmentDateTime = getSelectedDateTime();

    if (!appointmentDateTime || !appointmentDateTime.isValid() || !patientId) {
      return;
    }

    createAppointment(
      {
        AppointmentDate: appointmentDateTime
          .locale("en")
          .format("YYYY-MM-DD HH:mm"),
        PatientId: patientId,
      },
      {
        onSuccess: () => {
          refetchAllAppointmentsApis(queryClient);
          setIsSuccessMessageOpen(true);
        },

        onError: handleApiError,
      },
    );
  };

  // Handle the patient form.
  // Existing patient:
  // Create appointment immediately.
  // New patient:
  // Create the patient first, then create appointment.
  const handlePatientSubmit: SubmitHandler<PatientDetails> = (formData) => {
    // Do not set this variable as PatientDetails,
    // because CountryId is an API request property
    // and is not part of PatientDetails.
    const requestData = {
      ...formData,
      CountryId: countryId,
      Birthday: formData.Birthday,
    };

    setAppointmentFormData((previous) => ({
      ...previous,
      Account: formData,
    }));

    if (formData?.Id) {
      bookAppointment(Number(formData.Id));
      return;
    }

    createPatient(requestData, {
      onSuccess: (response) => {
        const newPatientId = Number(response?.Data?.Id);

        if (!newPatientId) {
          handleApiError({
            message: t("something_went_wrong"),
          });

          return;
        }

        const createdPatient: PatientDetails = {
          ...formData,
          Id: newPatientId,
        };

        setAppointmentFormData((previous) => ({
          ...previous,
          Account: createdPatient,
        }));

        queryClient.refetchQueries({
          queryKey: [EndPoints.GetMyPatient],
          exact: false,
        });

        bookAppointment(newPatientId);
      },

      onError: handleApiError,
    });
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // Handle patient loaded from child component (when URL has id)
  const handlePatientLoaded = (patientData: PatientDetails) => {
    setPatientFromChild(patientData);
    // If date and time are selected, book appointment immediately
    if (selectedDate && selectedTime && patientData?.Id) {
      bookAppointment(Number(patientData.Id));
    }
  };

  return (
    <>
      <main className="box">
        <TitleWithArrow title="add_new_appointment" />
        {/* Appointment content */}
        <section className="rounded-xl bg-input-field p-6 xl:m-10">
          <div className="mx-auto grid-cols-3 gap-3 overflow-y-auto max-md:max-h-[50dvh] max-md:w-2/3 max-sm:w-full md:grid">
            {/* Patient details */}
            <div><AppointmentPatientDetailsForm
              onSubmit={handlePatientSubmit}
              onPatientLoaded={handlePatientLoaded}
            />
              <div className="box mt-10 border !p-6 xl:hidden">
                {selectedDate && selectedTime ? (
                  <div className="flex flex-wrap items-center gap-2 font-bold text-primary-text">
                    <span className="font-normal">{t("appointment_on")}</span>
                    <span>{moment(selectedDate).format("MMM DD")}</span>
                    <span>{t("at")}</span>

                    <span>{formatTime(selectedTime)}</span>
                  </div>
                ) : (
                  <span className="text-sm text-body-text">
                    {t("select_appointment_date_and_time")}
                  </span>
                )}
              </div>
            </div>
            <GeneralCalendar
              className="box !w-full !max-w-none"
              onSelectedDate={handleSelectDate}
              customCellClassname={(date) => {
                const dayName = moment(date).format("dddd");

                const isPastDay = moment(date).isBefore(moment(), "day");

                const hasNoSchedule =
                  scheduledDays.length > 0 && !scheduledDays.includes(dayName);

                if (isPastDay || hasNoSchedule) {
                  return [
                    "!cursor-default",
                    "!bg-Gray",
                    "!text-text-muted",
                    "font-normal",
                    "opacity-50",
                    "disabled",
                  ].join(" ");
                }
                return "";
              }}
              isFull
              customHeadClassname={() => "p-2"}
            />

            {/* Appointment times */}
            <div className="flex xl:min-h-[360px] flex-col rounded-xl bg-white p-3">
              {isAvailableTimesLoading || isScheduleLoading ? (
                <div className="flex flex-1 items-center justify-center">
                  <Loader />
                </div>
              ) : availableTimes.length > 0 ? (
                <div className="show-scrollbar grid max-h-[340px] grid-cols-2 gap-3 overflow-y-auto p-1 sm:grid-cols-3">
                  {availableTimes.map(
                    (schedule: IScheduledTime, index: number) => {
                      const disabled =
                        !schedule?.isAvailable || isPastTime(schedule?.time);
                      const selected = schedule?.time === selectedTime;
                      return (
                        <button
                          key={`${schedule?.time}-${index}`}
                          type="button"
                          disabled={disabled}
                          onClick={() => handleSelectTime(schedule)}
                          className={[
                            "flex h-10 items-center justify-center",
                            "rounded-lg px-2 text-sm font-semibold",
                            "transition-all duration-150",
                            selected ? "bg-primary text-white shadow-none" : "",
                            !selected && !disabled
                              ? [
                                "bg-white",
                                "text-primary-text",
                                "shadow-sm",
                                "hover:bg-primary-click",
                                "hover:text-primary",
                              ].join(" ")
                              : "",
                            disabled
                              ? [
                                "cursor-not-allowed",
                                "bg-Gray",
                                "text-text-muted",
                                "opacity-50",
                                "shadow-none",
                              ].join(" ")
                              : "cursor-pointer",
                          ].join(" ")}
                        >
                          {formatTime(schedule?.time)}
                        </button>
                      );
                    },
                  )}
                </div>
              ) : (
                <div className="flex flex-1 flex-col items-center justify-center text-center">
                  <img
                    src={EmptyImage}
                    alt="empty schedule"
                    className="mb-3 w-24"
                  />

                  <p className="text-sm text-body-text">
                    {t("no_available_schedule_in_this_day")}
                  </p>
                </div>
              )}
            </div>

          </div>
          <div className="box ms-auto mt-3 md:w-2/3 max-xl:hidden">
            {selectedDate && selectedTime ? (
              <div className="flex flex-wrap items-center gap-2 font-bold text-primary-text">
                <span className="font-normal">{t("appointment_on")}</span>
                <span>{moment(selectedDate).format("MMM DD")}</span>
                <span>{t("at")}</span>

                <span>{formatTime(selectedTime)}</span>
              </div>
            ) : (
              <span className="text-sm text-body-text">
                {t("select_appointment_date_and_time")}
              </span>
            )}
          </div>
        </section>

        <div className="mt-6 flex flex-col-reverse justify-end gap-2 sm:flex-row">
          <GeneralButton
            type="button"
            onClick={handleCancel}
            className="!mb-0 !h-10 w-full rounded-lg sm:w-fit"
            color="lite"
            fontColor="body-text"
            title={t("cancel")}
          />

          <GeneralButton
            form={!currentPatient ? "patient-details-form" : undefined}
            type={!currentPatient ? "submit" : "button"}
            disabled={!selectedDate || !selectedTime || isSubmitting}
            isLoading={isSubmitting}
            onClick={
              currentPatient?.Id
                ? () => bookAppointment(Number(currentPatient.Id))
                : undefined
            }
            className="!mb-0 !h-10 w-full rounded-lg sm:w-[208px]"
            title={t("book_appointment")}
          />
        </div>
      </main>

      {/* Success modal */}
      {isSuccessMessageOpen && (
        <AppointmentMessage
          data={appointmentFormData as IAppointmentFormData}
          status="success"
          isOpen={isSuccessMessageOpen}
          onClose={() => {
            setIsSuccessMessageOpen(false);
            navigate(-1);
          }}
        />
      )}

      {/* Error modal */}
      {isErrorMessageOpen && (
        <AppointmentMessage
          message={errorMessage}
          status="error"
          isOpen={isErrorMessageOpen}
          onClose={() => {
            setIsErrorMessageOpen(false);
          }}
        />
      )}
    </>
  );
}

export default AddNewAppointmentPage;
